<template>
  <base-dialog v-bind="mappedAttrs" :fullscreen="isDialogFullscreen">
    <base-card
      :class="['d-flex flex-column h-full', cardClassComputed]"
      :style="cardStyles.join('; ')"
    >
      <!-- Title: fixed at top -->
      <base-card-title
        v-if="addCardTitle"
        :class="['flex-shrink-0', cardTitleClassComputed]"
      >
        <base-row no-gutters justify="center">
          <base-col class="font-weight-bold">
            <slot name="title" />
          </base-col>
          <slot name="headerActionButton" />
          <base-col
            v-if="addCardDismiss"
            :class="customCloseButtonClass"
            cols="auto"
          >
            <base-icon @click="close">mdi-close</base-icon>
          </base-col>
        </base-row>
      </base-card-title>

      <base-divider v-if="addDividers" class="ma-0 flex-shrink-0" />

      <!-- Content: scrollable -->
      <base-card-text
        :class="['flex-grow-1 d-flex', cardTextClassComputed]"
        style="overflow: hidden; min-height: 0"
      >
        <!-- Left slot -->
        <div
          v-if="$slots.left && showLeft"
          class="flex-shrink-0 overflow-y-hidden border-e"
          :style="{ width: leftWidth, height: '100%' }"
        >
          <slot name="left" />
        </div>

        <!-- Right slot (scrollable) -->
        <div class="flex-grow-1 pr-3 overflow-y-auto">
          <slot name="content" />
        </div>
      </base-card-text>

      <base-divider v-if="addDividers" class="ma-0 flex-shrink-0" />

      <!-- Actions: fixed at bottom -->
      <base-card-actions
        v-if="addCardActions"
        :class="['flex-shrink-0', cardActionsClassComputed]"
      >
        <slot name="action-button" />
      </base-card-actions>
    </base-card>
  </base-dialog>
</template>

<script lang="ts">
  import { defineComponent, computed, PropType } from 'vue'
  import {
    BaseRow,
    BaseCol,
    BaseIcon,
    BaseCard,
    BaseDialog,
    BaseCardTitle,
    BaseCardText,
    BaseCardActions,
    BaseDivider
  } from '@deltax/vuetify-wrapper-components'

  export default defineComponent({
    components: {
      BaseRow,
      BaseCol,
      BaseDialog,
      BaseCard,
      BaseCardTitle,
      BaseCardText,
      BaseCardActions,
      BaseDivider,
      BaseIcon
    },

    props: {
      roundedCard: {
        type: Boolean,
        default: false
      },
      cardStyles: {
        type: Array as PropType<string[]>,
        default: () => []
      },
      cardClasses: {
        type: Array as PropType<string[]>,
        default: () => []
      },
      addDividers: {
        type: Boolean,
        default: true
      },
      addCardTitle: {
        type: Boolean,
        default: true
      },
      addCardActions: {
        type: Boolean,
        default: true
      },
      addCardDismiss: {
        type: Boolean,
        default: true
      },
      contentClasses: {
        type: Array as PropType<string[]>,
        default: () => []
      },
      cardTitleClasses: {
        type: Array as PropType<string[]>,
        default: () => []
      },
      cardActionClasses: {
        type: Array as PropType<string[]>,
        default: () => []
      },
      isDialogFullscreen: {
        type: Boolean,
        default: false
      },
      customCloseButtonClass: {
        type: String,
        default: ''
      },
      useMobileSpacing: {
        type: Boolean,
        default: false
      },
      leftWidth: {
        type: String,
        default: '250px' // default width of left slot
      },
      showLeft: {
        type: Boolean,
        default: true
      }
    },

    emits: ['close'],

    setup(props, { emit, attrs }) {
      // Computed properties
      const mappedAttrs = computed(() => attrs)

      const cardClassComputed = computed(() => {
        return props.cardClasses.length > 0
          ? props.cardClasses
          : {
              'c-padding-8': !props.addDividers,
              'rounded-4': props.roundedCard
            }
      })

      const cardTitleClassComputed = computed(() => {
        return props.cardTitleClasses.length > 0
          ? props.cardTitleClasses
          : props.addDividers
            ? 'c-padding-r-5 c-padding-l-5 py-3'
            : 'pa-0 pb-3'
      })

      const cardTextClassComputed = computed(() => {
        return props.contentClasses.length > 0
          ? props.contentClasses
          : {
              'pa-0 pb-3': !props.addDividers,
              'c-padding-6': !props.useMobileSpacing && props.addDividers,
              'c-padding-2': props.useMobileSpacing && props.addDividers
            }
      })

      const cardActionsClassComputed = computed(() => {
        return props.cardActionClasses.length > 0
          ? props.cardActionClasses
          : {
              'pa-0 pt-4': !props.addDividers,
              'pa-3 mx-1': props.addDividers
            }
      })

      // Methods
      const close = () => {
        emit('close')
      }

      return {
        mappedAttrs,
        cardClassComputed,
        cardTitleClassComputed,
        cardTextClassComputed,
        cardActionsClassComputed,
        close
      }
    }
  })
</script>

<style scoped lang="scss">
  ::v-deep {
  .v-card__actions .v-btn+.v-btn {
    margin-left: 12px;
  }
    .fixed-title {
    position: fixed;
    width: 100%;
    top: 0;
    background: #fff;
    z-index: 2;
  }
  .fixed-actions {
    position: fixed;
    width: 100%;
    bottom: 0;
    background: #fff;
    z-index: 2;
  }
}
</style>
